import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Save, Lock, Unlock, MessageSquare, GitMerge, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { getTopicSamples, updateTopic, getTopics, mergeTopic, recomputeTopic, dissolveTopic, type Topic, type TopicSample } from '@/api/topics';
import { toast } from 'sonner';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';

interface TopicDrawerProps {
  topic: Topic | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTopicUpdate: (topic: Topic) => void;
  onMerged?: (removedId?: number) => void;
}

export function TopicDrawer({ topic, open, onOpenChange, onTopicUpdate, onMerged }: TopicDrawerProps) {
  const [samples, setSamples] = useState<TopicSample[]>([]);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [samplesPage, setSamplesPage] = useState(1);
  const [samplesHasMore, setSamplesHasMore] = useState(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingLocked, setEditingLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [mergePickerOpen, setMergePickerOpen] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [searchingTopics, setSearchingTopics] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState<Topic[]>([]);
  const [confirmMergeOpen, setConfirmMergeOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<Topic | null>(null);
  const [merging, setMerging] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [dissolving, setDissolving] = useState(false);
  const [confirmDissolveOpen, setConfirmDissolveOpen] = useState(false);

  // Reset form when topic changes
  useEffect(() => {
    if (topic) {
      setEditingName(topic.name);
      setEditingLocked(topic.locked);
      setHasChanges(false);
    }
  }, [topic]);

  // Load samples when drawer opens
  useEffect(() => {
    if (open && topic) {
      resetAndLoadSamples();
    }
  }, [open, topic]);

  // Load merge candidates when opening picker
  useEffect(() => {
    if (mergePickerOpen) {
      void fetchMergeCandidates(mergeSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergePickerOpen]);

  const resetAndLoadSamples = async () => {
    if (!topic) return;
    setSamples([]);
    setSamplesPage(1);
    setSamplesHasMore(true);
    await loadMoreSamples(1);
  };

  const loadMoreSamples = async (pageArg?: number) => {
    if (!topic || loadingSamples) return;
    const nextPage = pageArg ?? samplesPage;

    try {
      setLoadingSamples(true);
      const response = await getTopicSamples(topic.id, { page: nextPage, per_page: 20 });
      setSamples(prev => [...prev, ...response.samples]);
      setSamplesPage(nextPage + 1);
      if (response.meta) {
        setSamplesHasMore(nextPage < response.meta.total_pages);
      } else {
        // Fallback if meta missing: infer from page size
        setSamplesHasMore(response.samples.length > 0);
      }
    } catch (error) {
      console.error('Failed to load samples:', error);
      toast.error('Failed to load message samples');
    } finally {
      setLoadingSamples(false);
    }
  };

  const initObserver = useCallback(() => {
    if (!sentinelRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && samplesHasMore && !loadingSamples) {
        void loadMoreSamples();
      }
    });
    observerRef.current.observe(sentinelRef.current);
  }, [samplesHasMore, loadingSamples]);

  useEffect(() => {
    initObserver();
    return () => observerRef.current?.disconnect();
  }, [initObserver, samples, open]);

  const handleSave = async () => {
    if (!topic || !hasChanges) return;

    try {
      setSaving(true);
      const updates: { name?: string; locked?: boolean } = {};

      if (editingName !== topic.name) {
        updates.name = editingName;
      }
      if (editingLocked !== topic.locked) {
        updates.locked = editingLocked;
      }

      const response = await updateTopic(topic.id, updates);
      onTopicUpdate(response.topic);
      setHasChanges(false);
      toast.success('Topic updated successfully');
    } catch (error) {
      console.error('Failed to update topic:', error);
      toast.error('Failed to update topic');
    } finally {
      setSaving(false);
    }
  };

  const fetchMergeCandidates = async (query: string) => {
    if (!topic) return;

    try {
      setSearchingTopics(true);
      const resp = await getTopics({ q: query || undefined, page: 1, per_page: 100, sort: 'size', order: 'asc' });
      const filtered = resp.topics.filter((t) => t.id !== topic.id && t.name !== 'Unlabeled topic');
      setMergeCandidates(filtered);
    } catch (error) {
      console.error('Failed to search topics:', error);
      toast.error('Failed to search topics');
    } finally {
      setSearchingTopics(false);
    }
  };

  const handleSelectMergeTarget = (t: Topic) => {
    setSelectedTarget(t);
    setConfirmMergeOpen(true);
  };

  const handleConfirmMerge = async () => {
    if (!topic || !selectedTarget) return;
    try {
      setMerging(true);
      await mergeTopic(topic.id, selectedTarget.id);
      toast.success(`Merged into "${selectedTarget.name}"`);
      setConfirmMergeOpen(false);
      setMergePickerOpen(false);
      setSelectedTarget(null);
      onMerged?.(topic.id);
    } catch (error: any) {
      console.error('Failed to merge topics:', error);
      toast.error(error?.message || 'Failed to merge topics');
    } finally {
      setMerging(false);
    }
  };

  const handleRecompute = async () => {
    if (!topic) return;
    try {
      setRecomputing(true);
      const resp = await recomputeTopic(topic.id);
      onTopicUpdate(resp.topic);
      toast.success('Recomputed stats');
    } catch (error: any) {
      console.error('Failed to recompute stats:', error);
      toast.error(error?.message || 'Failed to recompute stats');
    } finally {
      setRecomputing(false);
    }
  };

  const handleConfirmDissolve = async () => {
    if (!topic) return;
    try {
      setDissolving(true);
      await dissolveTopic(topic.id);
      toast.success('Dissolve enqueued');
      setConfirmDissolveOpen(false);
      // Give the worker a moment, then trigger refresh via onMerged
      setTimeout(() => {
        onMerged?.(topic.id);
      }, 800);
    } catch (error: any) {
      console.error('Failed to dissolve topic:', error);
      toast.error(error?.message || 'Failed to dissolve topic');
    } finally {
      setDissolving(false);
    }
  };

  const handleNameChange = (value: string) => {
    setEditingName(value);
    setHasChanges(value !== topic?.name || editingLocked !== topic?.locked);
  };

  const handleLockedChange = (value: boolean) => {
    setEditingLocked(value);
    setHasChanges(editingName !== topic?.name || value !== topic?.locked);
  };

  const formatDistance = (distance: number) => {
    return distance.toFixed(3);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDistanceBadgeVariant = (distance: number) => {
    if (distance < 0.2) return 'default';
    if (distance < 0.4) return 'secondary';
    if (distance < 0.6) return 'outline';
    return 'destructive';
  };

  if (!topic) return null;

  return (
    <>
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="pb-4">
          <DrawerTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Topic Details
          </DrawerTitle>
          <DrawerDescription>
            View and edit topic information and message samples
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 pb-4 overflow-y-auto">
          <div className="space-y-6">
            {/* Topic Information */}
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="topic-name" className="text-sm font-medium">
                      Topic Name
                    </Label>
                    <Input
                      id="topic-name"
                      value={editingName}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="Enter topic name..."
                      className="mt-1"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="topic-locked"
                      checked={editingLocked}
                      onCheckedChange={handleLockedChange}
                    />
                    <Label htmlFor="topic-locked" className="flex items-center gap-2">
                      {editingLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                      {editingLocked ? 'Locked' : 'Unlocked'}
                    </Label>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setMergePickerOpen(true)}>
                      <GitMerge className="h-4 w-4 mr-2" />
                      Merge Into…
                    </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDissolveOpen(true)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Dissolve
                  </Button>
                    <Button variant="outline" size="sm" onClick={handleRecompute} disabled={recomputing}>
                      {recomputing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      {recomputing ? 'Recomputing…' : 'Recompute stats'}
                    </Button>
                  </div>

                  {hasChanges && (
                    <div className="flex justify-end">
                      <Button onClick={handleSave} disabled={saving} size="sm">
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Topic Statistics */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Topic Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Size</div>
                    <div className="text-lg font-semibold">{topic.size}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Avg Distance</div>
                    <div className="text-lg font-semibold font-mono">
                      {formatDistance(topic.avg_distance)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">P95 Distance</div>
                    <div className="text-lg font-semibold font-mono">
                      {formatDistance(topic.p95_distance)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Last Updated</div>
                    <div className="text-sm">{formatDate(topic.updated_at)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Message Samples */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Messages</h3>

                {loadingSamples ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : samples.length > 0 ? (
                  <div className="space-y-4">
                    {samples.map((sample) => (
                      <div key={sample.id} className="border rounded-lg p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm flex-1">{sample.content}</p>
                          <Badge variant={getDistanceBadgeVariant(sample.distance)} className="text-xs">
                            {formatDistance(sample.distance)}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{sample.user_email}</span>
                          <span>{formatDate(sample.created_at)}</span>
                        </div>
                      </div>
                    ))}
                    <div ref={sentinelRef} />
                    {loadingSamples && (
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    )}
                    {!samplesHasMore && samples.length > 0 && (
                      <div className="text-center text-xs text-muted-foreground">End of messages</div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No message samples found for this topic.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </DrawerContent>
    </Drawer>

    {/* Merge Picker */}
    <CommandDialog open={mergePickerOpen} onOpenChange={setMergePickerOpen}>
      <CommandInput
        placeholder="Search topics..."
        value={mergeSearch}
        onValueChange={(v) => {
          setMergeSearch(v);
          void fetchMergeCandidates(v);
        }}
      />
      <CommandList>
        <CommandEmpty>{searchingTopics ? 'Searching…' : 'No topics found.'}</CommandEmpty>
        <CommandGroup heading="Topics">
          {mergeCandidates.map((t) => (
            <CommandItem key={t.id} onSelect={() => handleSelectMergeTarget(t)}>
              <span className="truncate">{t.name}</span>
              <span className="ml-auto text-xs text-muted-foreground">size {t.size}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>

    {/* Confirm Merge */}
    <AlertDialog open={confirmMergeOpen} onOpenChange={setConfirmMergeOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Merge topic</AlertDialogTitle>
          <AlertDialogDescription>
            This will move all messages from the current topic into "{selectedTarget?.name}" and delete the current topic. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={merging}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmMerge} disabled={merging}>
            {merging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitMerge className="h-4 w-4 mr-2" />}
            {merging ? 'Merging…' : 'Confirm merge'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Confirm Dissolve */}
    <AlertDialog open={confirmDissolveOpen} onOpenChange={setConfirmDissolveOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Dissolve topic</AlertDialogTitle>
          <AlertDialogDescription>
            This will redistribute all messages in this topic to their nearest existing topics and then delete the topic. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={dissolving}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmDissolve} disabled={dissolving}>
            {dissolving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            {dissolving ? 'Dissolving…' : 'Confirm dissolve'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
